import * as Sentry from '@sentry/nextjs';
import { useApolloClient } from '@apollo/client/react';
import {
  use,
  type ChangeEvent,
  type Dispatch,
  type SetStateAction,
} from 'react';

import { env } from '@/env';

import { CurrentUserContext } from '@/context/CurrentUserContext';

import { getRestApiUrl } from '@/utils/getRestApiUrl';

import { useTrackIntercomEvent } from '@/hooks/useTrackIntercomEvent';

import { FILE_MANAGER_QUERY } from '../components/FileManager';
import type { FileManagerUploadingState } from '../components/file-manager/FileManagerUploadingBar';
import type { Segment } from '../components/file-manager/toolbar/breadcrumbs-bar/FileManagerBreadcrumbsBarSegment';

import { useCreateFolder } from './useCreateFolder';

interface FileResponse {
  _id: string;
  filename: string;
  created: string;
  createdBy: string;
  hidden: boolean;
  lastModified: string;
  lastModifiedBy: string;
  lastUploaded: string;
  lastUploadedBy: string;
  owner: string;
  s3Key: string;
  type: 'Group' | 'Service';
}

interface Props {
  segment: Segment | null | undefined;
  setUploadingState: Dispatch<SetStateAction<FileManagerUploadingState>>;
  setUploadedFile?: (uploadedFile: FileResponse) => void;
  rootOwnerId: string;
}

export const useFileUploader = ({
  segment,
  setUploadingState,
  setUploadedFile,
  rootOwnerId,
}: Props) => {
  const client = useApolloClient();
  const { currentUserId } = use(CurrentUserContext);
  const [createFolderMutation] = useCreateFolder(rootOwnerId);
  const trackIntercomEvent = useTrackIntercomEvent();

  const createFolder = async (opts: {
    owner: string;
    ownerType: string;
    parent: string | null | undefined;
    hidden: boolean;
    name: string;
  }): Promise<string> => {
    const { owner, ownerType, parent, name } = opts;

    const { data } = await createFolderMutation({
      variables: {
        data: {
          filename: name,
          owner,
          parent: parent ?? null,
          type: ownerType,
        },
      },
    });

    return data?.createFolder?.file?._id ?? '';
  };

  const createOrUpdateFile = async (file: {
    parent: string | null | undefined;
  }) => {
    await client.query({
      query: FILE_MANAGER_QUERY,
      variables: {
        fileId: file.parent ?? null,
        rootOwnerId: rootOwnerId ?? '',
        includeSharingGroups: false,
      },
      fetchPolicy: 'network-only',
    });
  };

  const generateFileUploader = async () => {
    const { FileUploader } = await import('@ortoo/upload');

    const fileUploader = new FileUploader({
      apiEndpoint: getRestApiUrl(),
      fileUploadUrl: env.NEXT_PUBLIC_FILE_UPLOAD_URL,
      fileUploadBucket: env.NEXT_PUBLIC_FILE_UPLOAD_BUCKET,
      awsFileUploadAccessKey: env.NEXT_PUBLIC_AWS_FILE_UPLOAD_ACCESS_KEY,
      accessToken: () => '',
      cookieAuth: true,
      createFolder,
      createOrUpdateFile,
    });

    fileUploader.on('submitted', (id: number, filename: string) => {
      const file = fileUploader.getFile(id);

      // Now the file properties won't be set at this point, due to the way
      // the underlying fineuploader works, so we'll do the next bit asynchronously
      setTimeout(() => {
        const parent = fileUploader._fileParams[id]?.parent;

        const newFile = {
          id,
          filename,
          uploadedBytes: 0,
          totalBytes: file.size,
          parent,
          startedAt: new Date(),
        };

        setUploadingState((prevState) => ({
          ...prevState,
          uploadingFiles: [...prevState.uploadingFiles, newFile],
        }));
      });
    });

    fileUploader.on(
      'progress',
      (
        id: number,
        _filename: string,
        uploadedBytes: number,
        totalBytes: number,
      ) => {
        setUploadingState((prevState) => {
          const uploadingFiles = prevState.uploadingFiles.map((file) => {
            if (file.id === id) {
              return {
                ...file,
                uploadedBytes,
                totalBytes,
              };
            }

            return file;
          });

          return {
            ...prevState,
            uploadingFiles,
          };
        });
      },
    );
    fileUploader.on(
      'complete',
      (
        id: number,
        _filename: string,
        res: {
          file?: FileResponse;
          error?: string;
        },
      ) => {
        const file = fileUploader.getFile(id);

        if (res.error) {
          setUploadingState((prevState) => {
            const uploadingFiles = prevState.uploadingFiles.map((file) => {
              if (file.id === id) {
                return {
                  ...file,
                  error: res.error,
                };
              }

              return file;
            });

            return {
              ...prevState,
              uploadingFiles,
            };
          });
        } else {
          const owner = segment?.ownerId ?? '';
          const ownerType = segment?.ownerType ?? '';
          trackIntercomEvent(
            `Uploaded ${ownerType}${file.type === 'zip' ? ' Zip' : ''} File`,
            {
              groupId: ownerType === 'Group' ? owner : undefined,
              serviceId: ownerType === 'Service' ? owner : undefined,
              fileName: _filename,
            },
          );

          if (setUploadedFile && res.file) {
            setUploadedFile(res.file);
          }
        }
      },
    );
    fileUploader.on('error', (id: number, filename: string, error: string) => {
      Sentry.captureException(new Error(error), {
        user: currentUserId ? { id: currentUserId } : undefined,
        tags: { uploadType: 'file' },
        extra: {
          rootOwnerId,
          ownerId: segment?.ownerId,
          ownerType: segment?.ownerType,
          parentId: fileUploader._fileParams[id]?.parent ?? segment?.fileId,
        },
      });

      setUploadingState((prevState) => {
        const uploadingFiles = prevState.uploadingFiles.map((file) => {
          if (file.id === id) {
            return {
              ...file,
              error,
            };
          }

          return file;
        });

        return {
          ...prevState,
          uploadingFiles,
        };
      });
    });

    return fileUploader;
  };

  const uploadFiles = async (files: File | ReadonlyArray<File>) => {
    const fileUploader = await generateFileUploader();

    const filesArray = Array.isArray(files) ? files : [files];

    return fileUploader.uploadFilesWithPath(filesArray, {
      owner: segment?.ownerId ?? undefined,
      ownerType: segment?.ownerType ?? undefined,
      hidden: segment?.hidden ?? false,
      parent: segment?.fileId ?? null,
    });
  };

  const handleUploadRequest = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files) {
      return;
    }

    return uploadFiles([...files]);
  };

  const handleUploadRequestFromDrop = async (files: ReadonlyArray<File>) => {
    return uploadFiles(files);
  };

  return { handleUploadRequest, handleUploadRequestFromDrop };
};
